/**
 * Predictive warm-up of derived project data.
 *
 * Opening a project costs one pass over its marks. This module pays that cost before the
 * navigation happens: on a pointer that is already heading for a link (certain intent), and
 * otherwise on the project the on-device usage model ranks highest (predicted intent). Both
 * paths converge on `deriveProject`, whose cache the screens read from.
 *
 * Warming is pure computation over data that is already in memory, so a wrong guess costs a
 * few milliseconds of idle time and nothing else.
 */

import { useCallback, useEffect, useMemo } from 'react';

import type { ProjectId } from '../../shared/types.js';
import { dayClock } from '../clock.js';
import { deriveProject, isWarm } from './derived.js';
import { useReplicaVersion, useStore } from './context.js';
import type { AppStore } from './store.js';
import { rankProjects, recordProjectOpen } from './usage-model.js';

/** Longest the browser may defer a warm-up before it is run anyway. */
const IDLE_TIMEOUT_MS = 200;

/** How many projects are warmed from the prediction alone. */
const PREDICTED_PROJECT_COUNT = 2;

/** Projects with a warm-up already scheduled, so a hover does not queue duplicate work. */
const scheduled = new Set<string>();

/** Runs `task` when the browser is next idle, or on a timer where idle callbacks are absent. */
function whenIdle(task: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => task(), { timeout: IDLE_TIMEOUT_MS });
    return;
  }
  setTimeout(task, 0);
}

/**
 * Computes and caches the derived bundle for `projectId` unless it is already warm.
 *
 * Safe to call on every pointer event: repeated calls for the same project and replica
 * version collapse into one computation.
 */
export function warmProject(store: AppStore, projectId: ProjectId): void {
  const day = dayClock.getDay();
  if (isWarm(store, projectId, day)) return;
  const key = `${projectId}:${store.getVersion()}:${day}`;
  if (scheduled.has(key)) return;
  scheduled.add(key);
  whenIdle(() => {
    scheduled.delete(key);
    deriveProject(store, projectId, day);
  });
}

/** Pointer and focus handlers that warm a project the moment intent is visible. */
export interface WarmHandlers {
  onPointerEnter: () => void;
  onPointerDown: () => void;
  onFocus: () => void;
}

/**
 * Returns handlers that warm `projectId`.
 *
 * `pointerenter` covers a mouse crossing the row, which lands well before the click;
 * `pointerdown` covers touch, where there is no hover but still around a tenth of a second
 * between the finger landing and the navigation; `focus` covers keyboard traversal.
 */
export function useWarmHandlers(projectId: ProjectId): WarmHandlers {
  const store = useStore();
  const warm = useCallback(() => warmProject(store, projectId), [store, projectId]);
  return useMemo(
    () => ({ onPointerEnter: warm, onPointerDown: warm, onFocus: warm }),
    [warm],
  );
}

/**
 * Warms the projects the usage model ranks highest among `candidates`.
 *
 * Runs whenever the candidate set or the replica changes, which is also when a previous
 * warm-up would have gone stale.
 */
export function usePredictiveWarm(candidates: readonly ProjectId[]): void {
  const store = useStore();
  const version = useReplicaVersion();
  const key = candidates.join(',');

  useEffect(() => {
    for (const projectId of rankProjects(candidates, PREDICTED_PROJECT_COUNT)) {
      warmProject(store, projectId);
    }
    // `key` stands in for the candidate list, whose identity changes on every render.
  }, [store, version, key]);
}

/**
 * Records that a project screen was opened, feeding the usage model.
 *
 * Recording happens once per mounted project, not once per section, so moving between the
 * roll call and the statistics of one project counts as a single visit.
 */
export function useRecordProjectOpen(projectId: ProjectId | undefined): void {
  useEffect(() => {
    if (projectId === undefined) return;
    recordProjectOpen(projectId);
  }, [projectId]);
}
